import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Locale, getTranslation, detectBrowserLocale, rtlLocales, localeNames } from "@/lib/translations";

interface LanguageContextType {
  country: string;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const countryCodeToLocaleMap: Record<string, Locale> = {
  gb: "en",
  in: "hi",
  br: "pt",
  de: "de",
  it: "it",
  mx: "es",
  es: "es",
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [country] = useState(() => {
    const FALLBACK_COUNTRY = "us";

    if (typeof location === "undefined") return FALLBACK_COUNTRY;

    // load country from ?country=<code> when available
    const params = new URLSearchParams(location.search);
    return params.get("country") ?? FALLBACK_COUNTRY;
  });

  const [locale, setLocale] = useState<Locale | null>(() => {
    if (typeof location === "undefined") return null;

    if (country && Object.keys(countryCodeToLocaleMap).includes(country)) {
      return countryCodeToLocaleMap[country];
    }

    const stored = localStorage.getItem("locale") as Locale | null;
    return stored || detectBrowserLocale();
  });
  const isRTL = locale ? rtlLocales.includes(locale) : false;

  useEffect(() => {
    if (!locale) return;
    localStorage.setItem("locale", locale);
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = locale;
  }, [locale, isRTL]);

  const t = (key: string): string => getTranslation(locale ?? "en", key);

  if (!locale) return null;

  return (
    <LanguageContext.Provider value={{ country, locale, setLocale, t, isRTL }}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
